import { Component, AfterViewInit, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AcmicService } from '@mapfre-design-library/src/public_api';
import { BillingNewService } from '@services/common/billing-new.service';
import { PolicyDataService } from '@services/data-services/policy-data.service';
import { CustomDateFormatPipe } from '../../../../../_helpers/_pipes/custom-date-format.pipe';
import { AddMissingPolicyComponent } from '../../../../common/add-missing-policy/add-missing-policy.component';
import { BillingAssistanceCardsComponent } from '../billing-assistance-cards/billing-assistance-cards.component';

export type PolicyTabType = 'active' | 'inactive';
@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, CustomDateFormatPipe, AddMissingPolicyComponent, BillingAssistanceCardsComponent],
  selector: 'app-billing-policy-selector',
  templateUrl: './billing-policy-selector.component.html',
  styleUrls: ['./billing-policy-selector.component.scss']
})
export class BillingPolicySelectorComponent implements OnInit, AfterViewInit {
  isAcmic: boolean;
  company: string;

  activePolicies: any[] = [];
  inactivePolicies: any[] = [];
  hasActive = false;
  hasInactive = false;

  selectedTab: PolicyTabType = 'active';

  @ViewChild('policyListEl') policyListEl?: ElementRef<HTMLDivElement>;

  thumbHeightPercent = 100;
  thumbTopPercent = 0;

  private isDraggingThumb = false;
  private dragStartY = 0;
  private dragStartScrollTop = 0;

  constructor(
    private acmicService: AcmicService,
    private policyDataService: PolicyDataService,
    private router: Router,
    private billingNewService: BillingNewService
  ) {
    this.isAcmic = this.acmicService.isACMIC();
  }

  ngOnInit(): void {
    this.getPolicies();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateThumb());
  }

  getPolicies() {
    this.policyDataService.$policyDetails.subscribe((policyList) => {
      if (policyList) {
        this.company = this.billingNewService.getCompany(policyList);
        this.activePolicies = [];
        this.inactivePolicies = [];
        (policyList || []).forEach((policy: any) => {
          if (policy?.policyStatus === 'FUTURE' || policy?.policyStatus === 'ACTIVE') {
            this.activePolicies.push(policy);
          } else {
            this.inactivePolicies.push(policy);
          }
        });
        this.hasActive = this.activePolicies.length > 0;
        this.hasInactive = this.inactivePolicies.length > 0;
        this.selectedTab = 'active';
        setTimeout(() => this.updateThumb());
      }
    });
  }

  get visiblePolicies(): any[] {
    if (this.hasActive && this.hasInactive) {
      return this.selectedTab === 'active' ? this.activePolicies : this.inactivePolicies;
    }
    return this.hasActive ? this.activePolicies : this.inactivePolicies;
  }

  get showTabs(): boolean {
    return this.hasActive && this.hasInactive;
  }

  get showStatusPill(): boolean {
    return !this.showTabs;
  }

  get isScrollable(): boolean {
    return this.visiblePolicies?.length > 3;
  }

  selectTab(tab: PolicyTabType) {
    this.selectedTab = tab;
    if (this.policyListEl) {
      this.policyListEl.nativeElement.scrollTop = 0;
    }
    setTimeout(() => this.updateThumb());
  }

  getStatusLabel(): string {
    return this.hasActive ? 'Active' : 'Inactive';
  }

  viewPolicy(policy: any) {
    const policyId = policy?.policynumber?.policyNumber ?? policy?.policyNumber;
    const billingPolicyData: BillingPolicy = {
      title: '',
      policyNumber: policyId,
      displayName: "name",
      policyData: []
    }
    if ((this.showTabs && this.selectedTab === 'active') || (!this.showTabs && this.hasActive))
      billingPolicyData.title = 'Active policies';
    else
      billingPolicyData.title = 'Inactive policies';
    this.router.navigate(['/billing-new', policyId, 'details'], {
      state: { billingPolicyData }
    });
  }

  getPolicyTypeLabel(policy: any): string {
    return policy?.displayType?.label ?? policy?.name ?? '';
  }

  getPolicyIcon(policy: any): string {
    return policy?.displayType?.icon ?? '';
  }

  onListScroll(): void {
    this.updateThumb();
  }

  private updateThumb(): void {
    const el = this.policyListEl?.nativeElement;
    if (!el) {
      return;
    }
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight) {
      this.thumbHeightPercent = 100;
      this.thumbTopPercent = 0;
      return;
    }
    this.thumbHeightPercent = Math.max((clientHeight / scrollHeight) * 100, 10);
    const maxScrollTop = scrollHeight - clientHeight;
    const maxTopPercent = 100 - this.thumbHeightPercent;
    this.thumbTopPercent = (scrollTop / maxScrollTop) * maxTopPercent;
  }

  onThumbMouseDown(event: MouseEvent): void {
    const el = this.policyListEl?.nativeElement;
    if (!el) {
      return;
    }
    this.isDraggingThumb = true;
    this.dragStartY = event.clientY;
    this.dragStartScrollTop = el.scrollTop;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.isDraggingThumb) {
      return;
    }
    const el = this.policyListEl?.nativeElement;
    if (!el) {
      return;
    }
    const { scrollHeight, clientHeight } = el;
    const trackHeight = clientHeight;
    const deltaY = event.clientY - this.dragStartY;
    const scrollableDistance = scrollHeight - clientHeight;
    const scrollDelta = (deltaY / trackHeight) * scrollHeight;
    el.scrollTop = Math.min(
      Math.max(this.dragStartScrollTop + scrollDelta, 0),
      scrollableDistance
    );
    this.updateThumb();
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    this.isDraggingThumb = false;
  }
}

export interface BillingPolicy {
  title: string;
  policyNumber: string;
  displayName: string;
  policyData: any[];
}
